import type { Pill } from "../../model/invoice-view";
import { currentLang, t } from "../../lib/sprache";
import { pill } from "./model";

/**
 * Betrieb (0101): Stand der Zeitpläne, der Aufrufe von Serverfunktionen und des Mailversands für die Betreiber-Konsole.
 * Datenquelle ist public.platform_ops_status (nur Plattform-Admins); hier nur Anzeige-Logik ohne Abfragen.
 */

export interface OpsJob {
  jobid: number;
  jobname: string;
  schedule: string;
  active: boolean;
  description_de: string | null;
  description_en: string | null;
  last_start: string | null;
  last_status: "succeeded" | "failed" | null;
  last_message: string | null;
  consecutive_failures: number;
}
export interface OpsCronFailure { jobid: number; jobname: string | null; status: string; message: string | null; start_time: string; end_time: string | null }
export interface OpsMissingJob { jobname: string; description_de: string; description_en: string; manual: boolean }
export interface OpsHttpFailure { id: number; function: string | null; status_code: number | null; timed_out: boolean; error: string | null; content: string | null; created: string }
export interface OpsHttpFunction {
  function: string | null; last_at: string; last_status: number | null; last_timed_out: boolean; last_error: string | null; last_failed: boolean;
  consecutive_failures: number;
}
export interface OpsMailOrg {
  org_id: string | null; name: string | null; failed: number; bounced: number; failed_7d: number; last_error: string | null; last_failed_at: string | null;
}
export interface OpsAlert {
  occasion: string; kind: "mails" | "cron" | "http" | "client" | (string & {}); count: number; threshold?: number; name?: string | null;
  description_de?: string | null; description_en?: string | null; at?: string | null; message?: string | null;
  status_code?: number | null; timed_out?: boolean | null; top?: { name?: string | null; n: number; message?: string; portal?: string }[];
  /** 0242 (bis 0243): offene Registrierung, damals noch als Warnung */
  email?: string | null;
}
export interface OpsSent { kind: "report" | "alert" | "signup_open" | "signup_confirmed"; occasion: string; sent_at: string; status: string | null; error: string | null }
export interface OpsSettings {
  support_email: string | null; language: "de" | "en"; time_zone: string; failed_mails_threshold: number; consecutive_threshold: number;
  cooldown_hours: number; edge_base_url_known: boolean;
}
export interface OpsStatus {
  generated_at: string;
  hours: number;
  settings: OpsSettings;
  cron: { available: boolean; jobs: OpsJob[]; failures: OpsCronFailure[]; missing: OpsMissingJob[] };
  http: { available: boolean; failures: OpsHttpFailure[]; functions: OpsHttpFunction[] };
  mail: { failed_last_hour: number; pending: number; oldest_pending_at: string | null; by_org: OpsMailOrg[] };
  alerts: OpsAlert[];
  sent: OpsSent[];
}

/** Die Datenbank liefert bigint als Text; Listen können fehlen. */
export function normalizeOps(raw: OpsStatus): OpsStatus {
  const n = (v: unknown) => Number(v ?? 0);
  return {
    ...raw,
    cron: {
      available: !!raw.cron?.available,
      jobs: (raw.cron?.jobs ?? []).map((j) => ({ ...j, jobid: n(j.jobid), consecutive_failures: n(j.consecutive_failures) })),
      failures: raw.cron?.failures ?? [],
      missing: raw.cron?.missing ?? [],
    },
    http: {
      available: !!raw.http?.available,
      failures: (raw.http?.failures ?? []).map((f) => ({ ...f, id: n(f.id) })),
      functions: (raw.http?.functions ?? []).map((f) => ({ ...f, consecutive_failures: n(f.consecutive_failures) })),
    },
    mail: {
      failed_last_hour: n(raw.mail?.failed_last_hour), pending: n(raw.mail?.pending), oldest_pending_at: raw.mail?.oldest_pending_at ?? null,
      by_org: (raw.mail?.by_org ?? []).map((m) => ({ ...m, failed: n(m.failed), bounced: n(m.bounced), failed_7d: n(m.failed_7d) })),
    },
    alerts: raw.alerts ?? [],
    sent: raw.sent ?? [],
  };
}

/** Ab so vielen Minuten Wartezeit gilt die Warteschlange als Stau (gleich dem Betriebsbericht, 0101). */
export const QUEUE_STUCK_MINUTES = 30;

export interface OpsKpis {
  cronFailures: number;
  cronStreaks: number;
  missingJobs: number;
  httpFailures: number;
  mailFailed: number;
  mailBounced: number;
  orgsWithFailures: number;
  pending: number;
  oldestMinutes: number | null;
  stuck: boolean;
}
export function opsKpis(s: OpsStatus, nowMs: number): OpsKpis {
  const oldest = s.mail.oldest_pending_at ? Math.max(0, Math.floor((nowMs - Date.parse(s.mail.oldest_pending_at)) / 60000)) : null;
  return {
    cronFailures: s.cron.failures.length,
    cronStreaks: s.cron.jobs.filter((j) => j.consecutive_failures >= s.settings.consecutive_threshold).length,
    missingJobs: s.cron.missing.length,
    httpFailures: s.http.failures.length,
    mailFailed: s.mail.by_org.reduce((a, m) => a + m.failed, 0),
    mailBounced: s.mail.by_org.reduce((a, m) => a + m.bounced, 0),
    orgsWithFailures: s.mail.by_org.filter((m) => m.failed + m.bounced > 0).length,
    pending: s.mail.pending,
    oldestMinutes: oldest,
    stuck: oldest !== null && oldest >= QUEUE_STUCK_MINUTES,
  };
}

export function jobDescription(j: { description_de?: string | null; description_en?: string | null }): string | null {
  return (currentLang() === "en" ? j.description_en : j.description_de) ?? null;
}

/** Zustand eines Zeitplans: Fehler in Folge ab Schwelle, zuletzt gescheitert, läuft, pausiert, noch kein Lauf. */
export function jobPill(j: OpsJob, threshold: number): Pill {
  if (!j.active) return pill(t("Pausiert", "Paused"), "neutral");
  if (j.consecutive_failures >= threshold) return pill(t(`${j.consecutive_failures}-mal hintereinander gescheitert`, `failed ${j.consecutive_failures} times in a row`), "error");
  if (j.last_status === "failed") return pill(t("Zuletzt gescheitert", "Last run failed"), "warning");
  if (j.last_status === "succeeded") return pill(t("Läuft", "Running"), "success");
  return pill(t("Noch kein Lauf", "No run yet"), "neutral");
}

export function httpFunctionPill(f: OpsHttpFunction, threshold: number): Pill {
  if (f.consecutive_failures >= threshold) return pill(t(`${f.consecutive_failures}-mal hintereinander gescheitert`, `failed ${f.consecutive_failures} times in a row`), "error");
  if (f.last_failed) return pill(t("Zuletzt gescheitert", "Last call failed"), "warning");
  return pill(t("Antwortet", "Responding"), "success");
}

/** „HTTP 502“, „Zeitüberschreitung“ oder „ohne Antwort“ */
export function httpStatusText(v: { status_code: number | null; timed_out: boolean }): string {
  if (v.timed_out) return t("Zeitüberschreitung", "Timeout");
  if (v.status_code === null || v.status_code === undefined) return t("ohne Antwort", "no response");
  return `HTTP ${v.status_code}`;
}

export function functionName(fn: string | null): string {
  return fn ?? t("ohne Zuordnung (Zeitplan ruft net.http_post direkt auf)", "unassigned (job calls net.http_post directly)");
}

/** Was eine auslösende Warnung meldet, als ein Satz. */
export function alertText(a: OpsAlert): string {
  if (a.kind === "mails") {
    return t(`${a.count} Mails in der letzten Stunde gescheitert oder zurückgekommen (Schwelle: mehr als ${a.threshold ?? "?"})`,
      `${a.count} emails failed or bounced in the last hour (threshold: more than ${a.threshold ?? "?"})`);
  }
  if (a.kind === "cron") return t(`Zeitplan ${a.name ?? "?"} ist ${a.count}-mal hintereinander gescheitert`, `Scheduled job ${a.name ?? "?"} failed ${a.count} times in a row`);
  if (a.kind === "http") return t(`Serverfunktion ${functionName(a.name ?? null)} ist ${a.count}-mal hintereinander gescheitert`, `Edge function ${functionName(a.name ?? null)} failed ${a.count} times in a row`);
  // Fehler aus dem Browser (0158): bisher fälschlich als Serverfunktion ohne Zahl dargestellt
  if (a.kind === "client") {
    return t(`${a.count} Fehler aus dem Browser in etwa der letzten Stunde (Schwelle: mehr als ${a.threshold ?? "?"})`,
      `${a.count} browser errors in about the last hour (threshold: more than ${a.threshold ?? "?"})`);
  }
  // Offene Registrierung (0242) ist seit 0243 keine Warnung mehr; ältere Einträge und unbekannte Arten lesbar statt „undefined-mal“
  if (a.kind === "signup") return t(`Registrierung ohne bestätigte Adresse: ${a.email ?? a.name ?? "?"}`, `Sign-up without confirmed address: ${a.email ?? a.name ?? "?"}`);
  return t(`Warnung ${a.occasion}`, `Warning ${a.occasion}`);
}

/** Betriebsmail im Protokoll: Bericht mit Tag oder Warnung mit Anlass. */
export function sentLabel(s: OpsSent): string {
  if (s.kind === "report") {
    const day = s.occasion.startsWith("report:") ? s.occasion.slice(7) : "";
    return t(`Betriebsbericht ${day}`, `Operations report ${day}`).trim();
  }
  const [kind, ...rest] = s.occasion.split(":");
  const what = rest.join(":");
  if (kind === "mails") return t("Warnung: viele gescheiterte Mails", "Warning: many failed emails");
  if (kind === "cron") return t(`Warnung: Zeitplan ${what}`, `Warning: scheduled job ${what}`);
  if (kind === "http") return t(`Warnung: Serverfunktion ${what === "?" ? functionName(null) : what}`, `Warning: edge function ${what === "?" ? functionName(null) : what}`);
  if (kind === "client") return t("Warnung: viele Fehler aus dem Browser", "Warning: many browser errors");
  if (kind === "signup") return t("Hinweis: Registrierung ohne bestätigte Adresse", "Notice: sign-up without confirmed address");
  if (kind === "signup_confirmed") return t("Hinweis: Registrierung über die Erinnerung bestätigt", "Notice: sign-up confirmed via the reminder");
  return s.occasion;
}

export function sentPill(s: OpsSent): Pill {
  switch (s.status) {
    case "sent": return pill(t("Versendet", "Sent"), "success");
    case "pending": case "held": return pill(t("Wartet", "Waiting"), "info");
    case "failed": case "bounced": return pill(t("Gescheitert", "Failed"), "error");
    case null: case undefined: return pill(t("Aufgeräumt", "Cleaned up"), "neutral");
    default: return pill(s.status, "neutral");
  }
}

/** Hinweise über der Seite: was eingerichtet werden muss, damit Bericht, Warnung und Zeitpläne funktionieren. */
export function opsNotices(s: OpsStatus): { tone: "warning" | "info"; text: string }[] {
  const out: { tone: "warning" | "info"; text: string }[] = [];
  if (!s.settings.support_email) {
    out.push({ tone: "warning", text: t("Keine Adresse für Betriebsmails: ohne support_email (Produkt-Standards) kommen weder der tägliche Betriebsbericht noch Sofortwarnungen an.",
      "No address for operations emails: without support_email (product defaults) neither the daily report nor instant warnings are sent.") });
  }
  if (!s.cron.available) {
    out.push({ tone: "info", text: t("Zeitpläne lassen sich hier nicht prüfen: pg_cron ist in dieser Datenbank nicht eingerichtet (lokal oder Demo).",
      "Scheduled jobs cannot be checked here: pg_cron is not set up in this database (local or demo).") });
  }
  if (!s.http.available) {
    out.push({ tone: "info", text: t("Aufrufe von Serverfunktionen lassen sich hier nicht prüfen: pg_net ist nicht eingerichtet.",
      "Edge function calls cannot be checked here: pg_net is not set up.") });
  }
  if (s.cron.available && !s.settings.edge_base_url_known) {
    out.push({ tone: "warning", text: t("Die Adresse des Projekts fehlt: Zeitpläne, die Serverfunktionen aufrufen (Absenderdomains, Speicher), scheitern, bis der Vault-Eintrag project_url angelegt ist (docs/EINSPIELPLAN.md Abschnitt 6).",
      "The project address is missing: jobs that call edge functions (sender domains, storage) fail until the vault entry project_url exists (docs/EINSPIELPLAN.md section 6).") });
  }
  const manual = s.cron.missing.filter((m) => m.manual);
  if (manual.length) {
    out.push({ tone: "warning", text: t(`Von Hand anzulegender Zeitplan fehlt: ${manual.map((m) => m.jobname).join(", ")} (docs/EINSPIELPLAN.md Abschnitt 6).`,
      `Job to be created by hand is missing: ${manual.map((m) => m.jobname).join(", ")} (docs/EINSPIELPLAN.md section 6).`) });
  }
  return out;
}

/** Wartezeit der ältesten Mail als Text. */
export function waitText(minutes: number | null): string {
  if (minutes === null) return t("keine Mail wartet", "no email waiting");
  if (minutes < 1) return t("älteste seit weniger als einer Minute", "oldest for less than a minute");
  if (minutes < 120) return t(`älteste seit ${minutes} Minuten`, `oldest for ${minutes} minutes`);
  const h = Math.floor(minutes / 60);
  return t(`älteste seit ${h} Stunden`, `oldest for ${h} hours`);
}

// ── Eigene Absenderdomains der Agenturen (0102) ────────────────────────────────────────────────────────

export interface OpsSenderDomain {
  org_id: string; org_name: string; domain: string; local_part: string; status: "requested" | "pending" | "verified" | "failed" | "none";
  checked_at: string | null; error: string | null; registered: boolean; module: boolean;
}
export interface OpsSenderRemoval { domain: string; reason: string; requested_at: string; attempts: number; last_error: string | null }
export interface OpsSenderDomains { ready: boolean; domains: OpsSenderDomain[]; removals: OpsSenderRemoval[] }

export function senderDomainPill(d: Pick<OpsSenderDomain, "status">, ready: boolean): Pill {
  switch (d.status) {
    case "verified": return ready ? pill(t("Aktiv", "Active"), "success") : pill(t("Bestätigt, Versand noch nicht freigeschaltet", "Verified, sending not enabled yet"), "info");
    case "pending": return pill(t("DNS-Prüfung läuft", "DNS check in progress"), "warning");
    case "requested": return pill(t("Wird angelegt", "Being created"), "info");
    case "failed": return pill(t("Prüfung gescheitert", "Verification failed"), "error");
    default: return pill(t("Keine", "None"), "neutral");
  }
}

export function removalReasonLabel(reason: string): string {
  switch (reason) {
    case "changed": return t("Domain geändert", "domain changed");
    case "removed": return t("Domain entfernt", "domain removed");
    case "deleted": return t("Agentur gelöscht", "agency deleted");
    case "plan": return t("Tarif ohne Modul", "plan without module");
    case "cancelled": return t("Agentur gekündigt", "agency cancelled");
    case "stale": return t("veraltete Anmeldung", "stale registration");
    default: return reason;
  }
}

// ── Zustellbarkeit (0104) ─────────────────────────────────────────────────────────────────────────────

export interface OpsMailHealthOrg {
  org_id: string; name: string; status: string; hourly_limit: number; sent_hour: number; pending: number; throttled: number;
  sent_7d: number | null; bounced_7d: number | null; bounce_percent: number | null;
}
export interface OpsMailHealth { limit: number; limit_trial: number; bounce_percent: number; bounce_min_mails: number; orgs: OpsMailHealthOrg[] }

/** Stand einer Agentur: gebremst (Mails warten am Stundenlimit), Rückläuferquote über der Schwelle, sonst in Ordnung. */
export function mailHealthPill(o: OpsMailHealthOrg, h: Pick<OpsMailHealth, "bounce_percent" | "bounce_min_mails">): Pill {
  if ((o.bounce_percent ?? 0) > h.bounce_percent && (o.sent_7d ?? 0) >= h.bounce_min_mails) {
    return pill(t(`${String(o.bounce_percent).replace(".", ",")} % Rückläufer`, `${o.bounce_percent} % bounces`), "error");
  }
  if (o.throttled > 0) return pill(t(`${o.throttled} warten am Stundenlimit`, `${o.throttled} waiting at the hourly limit`), "warning");
  return pill(t("In Ordnung", "OK"), "success");
}

// ── Fehler im Browser (0158) ──────────────────────────────────────────────────────────────────────────

export type ClientErrorKind = "error" | "rejection" | "render" | "chunk";
export interface ClientErrorGroup {
  fingerprint: string; kind: ClientErrorKind; portal: string; message: string; source: string | null; stack: string | null; path: string | null;
  release: string | null; browser: string | null; hits: number; people: number; orgs: string[]; first_at: string; last_at: string; days: number;
  resolved: boolean; resolved_at: string | null;
}
export interface ClientErrors { generated_at: string; days: number; threshold: number; last_hour: number; groups: ClientErrorGroup[] }

export function normalizeClientErrors(r: Partial<ClientErrors> | null | undefined): ClientErrors {
  return {
    generated_at: r?.generated_at ?? "", days: r?.days ?? 7, threshold: r?.threshold ?? 10, last_hour: Number(r?.last_hour ?? 0),
    groups: (r?.groups ?? []).map((g) => ({ ...g, hits: Number(g.hits), people: Number(g.people), orgs: g.orgs ?? [] })),
  };
}

export function clientErrorKindLabel(k: ClientErrorKind): string {
  switch (k) {
    case "render": return t("Seite abgestürzt", "Page crashed");
    case "rejection": return t("Unbehandelt", "Unhandled");
    case "chunk": return t("Nach Update", "After update");
    default: return t("Fehler", "Error");
  }
}

export function clientErrorPortalLabel(p: string): string {
  switch (p) {
    case "agentur": return t("Agentur", "Agency");
    case "creator": return "Creator";
    case "betrieb": return t("Betrieb", "Operations");
    case "konto": return t("Anmeldung/Konto", "Sign-in/account");
    default: return t("Öffentlich", "Public");
  }
}

/** Offen und heute mehrfach: Fehler; offen: Warnung; erledigt: Erfolg. */
export function clientErrorPill(g: ClientErrorGroup): Pill {
  if (g.resolved) return pill(t("Erledigt", "Resolved"), "success");
  if (g.people > 1 || g.hits >= 5) return pill(t("Offen", "Open"), "error");
  return pill(t("Offen", "Open"), "warning");
}
